import Util from '@/common/utils/Util';
import store from '@/store';
import router from '@/router';
import { CODE_ENV_LIST } from '@/common/constants/ConstCode';
import { modalList, show } from '@/common/utils/ControlModal';
import i18n from '@/i18n';
import { logSplashIntroError } from '@/common/utils/SplashIntroLogger';
import SplashIntroManager from '@/common/utils/SplashIntroManager';
const { global } = i18n;
const { t } = global;

/**
 * 앱 종료
 * @return ''
 * @class Util
 */
export const nativeFinish = () => {
  location.href = `native://action.${CODE_ENV_LIST.APP_SCHEMA}?cmd=finish`;
};

/**
 * 외부링크 URL 호출시
 * @return ''
 * @class Util
 */
export const nativeLink = (link) => {
  location.href = `native://action.${CODE_ENV_LIST.APP_SCHEMA}?cmd=browse&url=${encodeURIComponent(link)}`;
};

/**
 * SNS 로그인 콜백
 * @param json
 * @return {Promise<NavigationFailure|void>}
 */
window.loginForSnsCallback = async (json = '') => {
  const container = JSON.parse(json);
  if (container.code === 0) {
    await store.dispatch('layout/removeAllModalComponent');
    return;
  }

  if (!container.id) return;

  const userData = {
    userId: container.id,
    name: container?.name ?? '',
    email: container.email,
    phone: '',
    isAuthPhone: false,
    joinType: store.getters['user/getJoinType']
  };

  if (await signIn(userData)) return await routeAfterSnsSignIn();
  else {
    if (!(await store.dispatch('user/SIGN_UP', userData))) return;
    if (await signIn(userData)) return await routeAfterSnsSignIn();
  }

};

const signIn = async (userData) => {
  const { userId, userPwd } = await store.dispatch('user/SIGN_IN', userData);
  return userId && userPwd;
};

const routeAfterSnsSignIn = async () => {
  if (await DynamicLinkManager.replayPendingPush()) return;
  return router.replace('/');
};

/**
 * 디바이스 정보 가져오기
 * @param deviceType
 * @param deviceId
 * @param pushToken
 * @param appVersion
 * @param os
 * @returns {Promise<void>}
 */
window.nativeSetDeviceInfo = async (deviceType, deviceId, pushToken, appVersion, os) => {
  const normalizedDeviceId = String(deviceId ?? '');
  const currentDeviceInfo = store.getters['user/getDeviceInfo'] ?? {};
  let deviceInfo = {
    deviceNo: null,
    userNo: null,
    deviceType: '',
    deviceId: '',
    pushToken: '',
    appVersion: '',
    os: ''
  };

  /* NOTICE: deviceType 1: 안드로이드, 2: IOS
      (IOS 에서 초기에는 'IOS'로 넘어오지만, 이후 '2'로 넘어오기 때문에 'IOS'로 변경)
   */
  deviceType = deviceType == 1 || deviceType === 'ANDROID' || !normalizedDeviceId.includes('-') ? 'ANDROID' : 'IOS';

  deviceInfo['deviceType'] = deviceType;
  deviceInfo['deviceId'] = normalizedDeviceId;
  deviceInfo['pushToken'] = pushToken || currentDeviceInfo.pushToken || '';
  deviceInfo['appVersion'] = appVersion;
  deviceInfo['os'] = os;
  // localStorage.deviceInfo = JSON.stringify(deviceInfo);

  store.commit('user/setDeviceInfo', deviceInfo);
  Util.nativeFirstRun('nativeFirstInfo');
};

window.nativeUpdatePushToken = async (pushToken = '') => {
  const normalizedPushToken = String(pushToken ?? '');
  if (!normalizedPushToken) return;

  const deviceInfo = store.getters['user/getDeviceInfo'] ?? {};
  if (deviceInfo.pushToken === normalizedPushToken) return;

  store.commit('user/setDeviceInfo', {
    ...deviceInfo,
    pushToken: normalizedPushToken
  });

  try {
    await store.dispatch('user/SYNC_FCM_KEY', { notLoading: true });
  } catch (error) {
    console.warn('nativeUpdatePushToken failed', error);
  }
};

/** 처음실행시 접근권한 페이지로 띄움 **/
window.nativeFirstInfo = async (val) => {
  console.warn('nativeFirstInfo()');
  const isFirstRun = val == 0;
  try {
    const shouldReloadForWebCache = await store.dispatch('user/CHECK_WEB_CACHE_VERSION', {
      skipReloadWhenVersionMissing: isFirstRun
    });
    if (shouldReloadForWebCache) return;
  } catch (error) {
    console.warn('CHECK_WEB_CACHE_VERSION failed', error);
    logSplashIntroError('WEB_CACHE_VERSION_CHECK_FAILED', error, { callbackValue: val });
  }

  // 최초 실행시 0, 그 이후 1
  // MEMO: hasUpdate
  let hasUpdate = false;
  try {
    hasUpdate = await store.dispatch('user/HAS_UPDATE');
  } catch (error) {
    console.warn('HAS_UPDATE failed', error);
    logSplashIntroError('HAS_UPDATE_FAILED', error, { callbackValue: val });
  }
  if (isFirstRun) {
    DeviceManager.reduce(DeviceManager.actions.FIRST_RUN);
  } else if (hasUpdate) {
    DeviceManager.reduce(DeviceManager.actions.UPDATE);
  } else DeviceManager.reduce();
};

/** 푸시로 인한 router 이동 **/
window.fnRouterPush = (callbackParams) => {
  console.log('window.fnRouterPush :: ', callbackParams);
  DynamicLinkManager.doPushAction(callbackParams).catch((error) => {
    console.warn('fnRouterPush failed', error);
  });
  // store.commit('pushRoute/setPath', callbackParams);
};

window.fnBack = async () => {
  // 결제 화면에서 뒤로가기 할때 (안드로이드)
  // 0. modalAlert이 있는지
  // 1. modal이 있는지
  // 2. side-bar 열려 있는지
  // 3. 종료해야할 페이지인지
  // 4. router.go(-1)
  const modalAlerts = store.getters['layout/getModalAlertComponents'];
  const modals = store.getters['layout/getModalComponents'];
  const isShowSideBar = store.getters['layout/getIsShowSideBar'];
  if (!Util.isEmpty(modalAlerts)) {
    await store.dispatch('layout/closeModalAlertComponent', {
      index: modalAlerts.length - 1
    });
    return;
  }
  if (!Util.isEmpty(modals)) {
    await store.dispatch('layout/closeModalComponent', {
      index: modals.length - 1
    });
    return;
  }
  if (isShowSideBar) {
    await store.dispatch('layout/updateIsShowSideBar', {
      isShowSideBar: false
    });
    return;
  }
  const currentPath = window.path || window.location.pathname;
  const isRoot = !Util.isEmpty(window.routeQuery?.isRoot) && Number(window.routeQuery.isRoot) > 0;
  const isChatRoom = /^\/room\/\d+\/(ORDER|INQUIRY)$/i.test(currentPath);

  if (isRoot && isChatRoom) {
    router.replace('/chat/order?isRoot=1');
    return;
  }
  if (isRoot) {
    nativeFinish();
    return;
  }

  const pathList = ['/shop/dashboard', '/shop/store', '/promotion', '/new', '/cart'];
  if (/^\/shop\/\d+\/product$/.test(currentPath)) {
    const toPath = store.getters['layout/getFromStorePath'];
    if (toPath) {
      if (pathList.includes(toPath) || toPath.includes('/search/result')) router.replace(`${toPath}`);
      else router.replace(`${toPath}?isRoot=1`);
    } else {
      router.replace('/main?isRoot=1');
    }
  } else if (
    /^\/promotion$/.test(currentPath) ||
    /^\/new$/.test(currentPath) ||
    currentPath.includes('/search/result')
  ) {
    router.replace('/main?isRoot=1');
  } else {
    router.go(-1);
  }
};

window.setEncryptValue = async (value, value2) => {
  // juminCount 먼저
  if (!Util.isEmpty(value2)) {
    await store.dispatch('car/updateJuminCount', {
      juminCount: Number(value2)
    });
  }
  await store.dispatch('car/updateCarInsureDTOEncryptedJumin', {
    encryptedJumin: value
  });
};

let callbackScreenOnFunction;
window.callbackScreenOn = () => {
  if (callbackScreenOnFunction) {
    callbackScreenOnFunction();
  }
};

let callbackScreenOffFunction;
window.callbackScreenOff = () => {
  if (callbackScreenOffFunction) {
    callbackScreenOffFunction();
  }
};

import { reverseGeocode } from '@/common/utils/GoogleGeoCoder';
import DynamicLinkManager from '@/common/utils/DynamicLinkManager';
import DeviceManager from '@/common/utils/DeviceManager';
import { scrollDown } from '@/views/chat/lobby/chatUtils';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

window.getLocation = async (permitted, lat, lng) => {
  try {
    let tmpLat = Number(lat);
    let tmpLng = Number(lng);

    if (permitted != 1) {
      tmpLat = 10.7552928;
      tmpLng = 106.3655816;
    }

    if ((permitted === -1 && lat === -1, lng === -1)) {
      Util.nativeGpsSetting();
    }

    let geoPoint = { address: '', latitude: tmpLat, longitude: tmpLng };
    try {
      geoPoint = await reverseGeocode(tmpLat, tmpLng);
    } catch (error) {
      console.warn('reverseGeocode failed', error);
      logSplashIntroError('REVERSE_GEOCODE_FAILED', error, { permitted, lat: tmpLat, lng: tmpLng });
    }

    store.commit('user/setUserPosition', geoPoint);
  } finally {
    SplashIntroManager.clearLocationPending();
  }
};

window.checkLocationPermission = (isPermitted) => {
  console.log(isPermitted);
  if (isPermitted == '-1') return;
};

window.onDynamicLink = (dynamicLinkValue = '') => {
  console.warn('DynamicLink Value', dynamicLinkValue);
  if (!dynamicLinkValue) return;

  store.commit('dynamiclink/setPath', dynamicLinkValue.replace(CODE_ENV_LIST.DYNAMIC_DOMAIN, ''));
  console.log('window.onDynamicLink :: ', dynamicLinkValue);
  DynamicLinkManager.doDynamicAction(dynamicLinkValue);
};

window.isOnAlert = async (isOn) => {
  console.log('isOnAlert :: ', isOn, typeof isOn);
  let response = isOn == true || isOn == 1 || isOn == 'true';
  store.commit('user/setIsOnAlert', response);
};

//iOS
window.isScreenOn = async (on) => {
  Util.nativeFetchIsOnAlert('isOnAlert');

  const { roomNo } = store.getters['chat/getSelectedRoom'];
  // const { userNo } = store.getters['user/getUserData'];
  // if (!roomNo || !userNo) return;
  if (!roomNo) return;

  // awake 시점에 채팅이 최신화가 아니면 마지막 index 로 최신 bubble을 불러오기 때문에 2개 보내진것 처럼 보일 수 있음
  if (JSON.parse(on)) {
    await iAmOnline();
    await awake(roomNo);
  } else {
    await iAmOffline();
    await hibernate(roomNo);
  }
};

//AOS
window.onBackground = async () => {
  console.warn('AOS onBackground');
  const { roomNo } = store.getters['chat/getSelectedRoom'];
  const { userNo } = store.getters['user/getUserData'];
  if (!roomNo || !userNo) return;
  await iAmOffline();
  await hibernate(roomNo);
};

window.onForeground = async () => {
  console.warn('AOS onForeground');
  store.commit('chat/setImageUploadState', false);
  Util.nativeFetchIsOnAlert('isOnAlert');
  const { roomNo } = store.getters['chat/getSelectedRoom'];
  // const { userNo } = store.getters['user/getUserData'];
  // if (!roomNo || !userNo) return;
  if (!roomNo) return;
  await iAmOnline();
  await awake(roomNo);
};

window.getPushState = (isOn) => {
  //prepare
  if (isOn == '1' || isOn == 'true' || isOn == true) {
    const prefix = 'modal.check_push_state';
    const data = {
      title: t(`${prefix}.title`),
      text: t(`${prefix}.text`),
      confirmText: t(`${prefix}.confirmText`),
      rejectText: t(`${prefix}.rejectText`),
      confirmCallback: Util.nativePushSetting
    };
    show(modalList.Confirm, data);
  }
};

const hibernate = async (roomNo) => {
  await store.dispatch('chat/hibernate', roomNo);
  await store.dispatch('sse/emitterHibernate');
};

/**
 * 앱이 백그라운드에서 포그라운드로 전환될 때
 */
const awake = async (roomNo) => {
  const pathname = window.location.pathname;
  const isRoomPath = /^\/room\/\d+\/(ORDER|INQUIRY)$/i.test(pathname);
  if (!isRoomPath) return;

  await store.dispatch('sse/emitterWake');
  await store.dispatch('chat/wake', roomNo);

  await store.dispatch('chat/fetchChatList', { roomNo });
  await store.dispatch('chat/fetchLastChat');

  scrollDown('chat_box');
};

const iAmOnline = async () => {
  await store.dispatch('user/I_AM_ONLINE');
};
const iAmOffline = async () => {
  await store.dispatch('user/I_AM_OFFLINE');
};
